import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LuKey, LuTrash2 } from "react-icons/lu";
import { api, ApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { SkeletonTable } from "@/components/ui/skeleton";

export function ApiKeysPage() {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ["console", "admin", "api-keys"],
    queryFn: () => api.adminApiKeys.list(),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.adminApiKeys.revoke(id),
    onSuccess: () => {
      toast.success("Revoked.");
      setConfirm(null);
      qc.invalidateQueries({ queryKey: ["console", "admin", "api-keys"] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Failed."),
  });

  return (
    <>
      <PageHeader
        icon={LuKey}
        title="API keys"
        description="Last-used timestamps + per-key revocation. Revoking is immediate; the user has to mint a new key."
      />
      <div className="p-8">
        {list.isLoading && <SkeletonTable rows={6} cols={5} />}
        {list.data && (
          <Card>
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg)]/40">
                <tr className="text-left text-[var(--color-text-subtle)] border-b border-[var(--color-border)]">
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em]">Name</th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em]">User</th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em]">Last used</th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em]">Created</th>
                  <th className="px-5 py-3 text-[11px] uppercase tracking-[0.12em]">Status</th>
                  <th className="px-5 py-3 w-12" />
                </tr>
              </thead>
              <tbody>
                {list.data.keys.map((k) => {
                  const stale = !k.last_used_at || (Date.now() - new Date(k.last_used_at).getTime()) > 90 * 86400_000;
                  return (
                    <tr key={k.id} className="border-b border-[var(--color-border)] last:border-0 hover:bg-[var(--color-elevated)]/30">
                      <td className="px-5 py-3 text-[var(--color-text)] truncate max-w-[200px]">
                        {k.name}
                      </td>
                      <td className="px-5 py-3 text-[var(--color-text-muted)] truncate max-w-[200px]">
                        {k.user_email}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-[var(--color-text-subtle)] tabular-nums">
                        {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : "never"}
                        {stale && !k.revoked_at && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-[#fcd34d]">
                            stale
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 font-mono text-xs text-[var(--color-text-subtle)] tabular-nums">
                        {new Date(k.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-3">
                        <span
                          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border ${
                            k.revoked_at
                              ? "bg-[rgba(237,66,69,0.1)] border-[rgba(237,66,69,0.3)] text-[#fca5a5]"
                              : "bg-[rgba(66,184,131,0.1)] border-[rgba(66,184,131,0.3)] text-[#6ee7b7]"
                          }`}
                        >
                          {k.revoked_at ? "Revoked" : "Active"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        {!k.revoked_at && (
                          <button
                            type="button"
                            onClick={() => setConfirm(k.id)}
                            className="text-[var(--color-text-subtle)] hover:text-[var(--color-error)] transition-colors p-1"
                            title="Revoke"
                          >
                            <LuTrash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </div>
      <ConfirmDialog
        open={!!confirm}
        title="Revoke API key?"
        description="The user's existing requests with this key will start failing immediately. This can't be undone."
        confirmLabel="Revoke"
        destructive
        pending={revoke.isPending}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm && revoke.mutate(confirm)}
      />
    </>
  );
}
